import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';

import { idFromObject } from '@sorare/core/src/gql/idFromObject.ts';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { ensureHexColor } from '@sorare/core/src/lib/colors';
import { isType } from '@sorare/core/src/lib/gql';

import { RevealPackCardsDialog } from '#src/components/commonCard/RevealPackCardsDialog/index.tsx';

import type {
  PurchasedPacksRevealQuery,
  PurchasedPacksRevealQueryVariables,
} from './__generated__/index.graphql.ts';

const PURCHASED_OFFER_QUERY = gql`
  query PurchasedPacksRevealQuery($offerId: String!) {
    tokens {
      primaryOffer(id: $offerId) {
        id
        purchasedItems {
          slug
          ... on PackConfigPurchasableItem {
            slug
            pack {
              id
              cards {
                slug
                ...RevealPackCardsDialog_anyCardInterface
              }
            }
            cardPack {
              slug
              pictureUrl
              primaryColor: color(variant: PRIMARY)
              secondaryColor: color(variant: SECONDARY)
              cardSet {
                slug
                ...RevealPackCardsDialog_cardSet
              }
            }
          }
        }
      }
    }
  }
  ${RevealPackCardsDialog.fragments.anyCardInterface}
  ${RevealPackCardsDialog.fragments.cardSet}
` as TypedDocumentNode<
  PurchasedPacksRevealQuery,
  PurchasedPacksRevealQueryVariables
>;

type Props = {
  offerId: string;
  onAllRevealed: () => void;
};

export const PurchasedPacksReveal = ({ offerId, onAllRevealed }: Props) => {
  const [currentPackIndex, setCurrentPackIndex] = useState(0);
  const [open, setOpen] = useState(true);
  const { data } = useQuery(PURCHASED_OFFER_QUERY, {
    variables: { offerId: idFromObject(offerId) },
  });

  const purchasedPacks =
    data?.tokens?.primaryOffer?.purchasedItems?.filter(item =>
      isType(item, 'PackConfigPurchasableItem')
    ) ?? [];

  const currentPack = purchasedPacks[currentPackIndex] ?? null;

  if (!currentPack?.pack || !currentPack.cardPack) return null;

  const handleClose = () => setOpen(false);

  const handleClosed = () => {
    if (currentPackIndex < purchasedPacks.length - 1) {
      setCurrentPackIndex(prev => prev + 1);
      setOpen(true);
    } else {
      onAllRevealed();
    }
  };

  return (
    <RevealPackCardsDialog
      key={currentPack.pack.id}
      open={open}
      onClosed={handleClosed}
      cards={currentPack.pack.cards ?? []}
      onContinue={handleClose}
      packId={currentPack.pack.id}
      packPictureUrl={currentPack.cardPack.pictureUrl || ''}
      primaryColor={ensureHexColor(currentPack.cardPack.primaryColor || '')}
      secondaryColor={ensureHexColor(currentPack.cardPack.secondaryColor || '')}
      cardSet={currentPack.cardPack.cardSet}
      showPlayerDetails
    />
  );
};
